import type { Optional } from 'utility-types'
import React, { type FC, useCallback, useMemo } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { Replay } from '@mui/icons-material'

import { ConnectwareError, CybusLogLevel, type CybusLogSource, Translation } from '../../../domain'

import type { LogOptions } from '../../../application'

import { useAsyncCallback } from '../Callback'
import { Table as BaseTable, Button, Chip, DateTimeRangeFilter, type DateTimeRangeValue, DownloadIcon, Dropdown } from '../common'
import { FormattedDateTime, FormattedTranslation, useTranslator } from '../Internationalization'
import { useAppUsecase } from '../State'
import { LogLevel } from './LogLevel'
import { Modal } from './Modal'
import {
    LogsProvider,
    type LogsReloadProps,
    useDateRangeDispatcher,
    useEndDate,
    useLevels,
    useLevelTogglerDispatcher,
    useLogs,
    useLogsLoader,
    useResource,
    useSelectedLogDispatcher,
    useSource,
    useStartDate,
} from './State'

const DowloadRawLogsButton: FC = () => {
    const containerLogs = useAppUsecase('containerLogsUsecase')

    const resource = useResource()
    const source = useSource()

    const [downloadRawlogs] = useAsyncCallback(() => containerLogs.downloadRawlogs(source, resource), [containerLogs, source, resource])

    return (
        <Button variant="secondary" size="medium" startIcon={<DownloadIcon />} onClick={downloadRawlogs}>
            <FormattedTranslation id={Translation.DOWNLOAD_RAW_LOGS} />
        </Button>
    )
}

const RefreshLogsButton: FC = () => {
    const loadLogs = useLogsLoader()
    return (
        <Button variant="secondary" size="medium" startIcon={<Replay />} onClick={loadLogs}>
            <FormattedTranslation id={Translation.REFRESH_LOGS} />
        </Button>
    )
}

const logItemStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 2 }

const LogTypesFilter: FC = () => {
    const levelFilters = useLevels()
    const toggle = useLevelTogglerDispatcher()

    const filterOptions = Object.values(CybusLogLevel).map((level) => ({
        value: level,
        label: <FormattedTranslation id={Translation.LOG_LEVEL} values={{ level }} />,
    }))

    return (
        <Box sx={logItemStyle}>
            <Dropdown<CybusLogLevel>
                buttonLabel={<FormattedTranslation id={Translation.FILTER} />}
                options={filterOptions}
                size="medium"
                data-testid="filter"
                value={levelFilters}
                onChange={toggle}
            />

            {levelFilters.map((level) => (
                <Chip
                    data-testid={`log-level-chip-${level}`}
                    key={level}
                    label={<FormattedTranslation id={Translation.LOG_LEVEL} values={{ level }} />}
                    onDelete={() => toggle(level)}
                    variant="neutral"
                />
            ))}
        </Box>
    )
}

export const DateTimeRangeFilterWrapper: FC = () => {
    const startDate = useStartDate()
    const endDate = useEndDate()
    const setDateRange = useDateRangeDispatcher()

    const handleApply = useCallback(
        ({ start, end }: DateTimeRangeValue) => {
            setDateRange({ start, end })
        },
        [setDateRange]
    )

    /**
     * The current local day is the latest selectable date; future days are disabled since logs
     * are not available for them yet. Memoized on the current day (not on every render) so a page
     * left open across midnight still picks up the new day.
     */
    const today = new Date().toDateString()
    const disableAfter = useMemo(() => {
        const date = new Date()
        date.setHours(0, 0, 0, 0)
        return date
    }, [today])

    const value = useMemo(
        () => ({
            start: startDate ?? null,
            end: endDate ?? null,
        }),
        [startDate, endDate]
    )

    return <DateTimeRangeFilter value={value} onApply={handleApply} disableAfter={disableAfter} />
}

type InternalProps = (Readonly<{ id: string }> | Readonly<{ downloadSuffix: string }>) & Readonly<{ showDownloadRawLogs?: boolean, showServiceId?: boolean }>

const InternalTable: FC<InternalProps> = ({ showDownloadRawLogs = false, showServiceId = false, ...props }) => {
    const containerLogs = useAppUsecase('containerLogsUsecase')
    const translation = useTranslator()
    const selectLog = useSelectedLogDispatcher()

    const logs = useLogs()
    const loadLogs = useLogsLoader()

    const loaded = logs === null || ConnectwareError.is(logs) ? null : logs
    const data = loaded ?? []
    const csvFileName = 'downloadSuffix' in props ? props.downloadSuffix : props.id

    return (
        <BaseTable
            loading={logs === null}
            error={ConnectwareError.is(logs) ? { error: logs, onRetry: loadLogs } : undefined}
            data={data.map((l) => ({ ...l, date: l.timestamp }))}
            columns={{
                level: { label: Translation.LEVEL, customCellRender: (level) => <LogLevel level={level} />, sort: true },
                timestamp: {
                    label: Translation.TIME,
                    customCellRender: (date) => <FormattedDateTime date={date} format="datetime" />,
                    sort: (t) => t.getTime(),
                    type: 'datetime',
                },
                ...(showServiceId ? { serviceId: { label: translation.formatTranslation(Translation.SERVICE, { count: 1 }), sort: true } } : {}),
                message: { label: Translation.MESSAGE, sort: true },
            }}
            pagination={{ pageSizeOptions: [50] }}
            sortOrder={{ name: 'timestamp', direction: 'desc' }}
            filters={
                <>
                    <LogTypesFilter />
                    <DateTimeRangeFilterWrapper />
                </>
            }
            utilities={
                <>
                    {showDownloadRawLogs && <DowloadRawLogsButton />}
                    <RefreshLogsButton />
                </>
            }
            onRowClick={selectLog}
            search
            translations={{ noResultsOrEmptyTable: Translation.NO_LOGS_FOUND }}
            onDownloadCSV={loaded ? () => containerLogs.downloadCSV(loaded, csvFileName, showServiceId) : undefined}
        >
            <Modal />
        </BaseTable>
    )
}

type ExternalProps = InternalProps & Readonly<{ type: CybusLogSource }> & Optional<Pick<LogOptions, 'lines'>> & LogsReloadProps

export const Table: FC<ExternalProps> = ({ type, lines, reloadOn, ...props }) => (
    <LogsProvider resource={'id' in props ? props.id : null} source={type} lines={lines} reloadOn={reloadOn}>
        <InternalTable {...props} />
    </LogsProvider>
)
